/**
 * SearchAiz - Unified Search System
 *
 * Main entry point for the SearchAiz component system.
 *
 * Quick Start:
 * ```tsx
 * import { SearchAiz } from '@/components/search-aiz';
 *
 * // Header search with command palette (⌘K)
 * <SearchAiz context="header" />
 *
 * // Full search with custom settings
 * <SearchAiz
 *   mode="full"
 *   context="fullPage"
 *   developerSettings={{
 *     ai: { rerank: { enabled: true } },
 *     sources: { available: ['knowledge_base', 'calls'] }
 *   }}
 * />
 * ```
 */

// Main components
export { SearchAiz, SearchAizWithProvider } from './SearchAiz';

// Providers
export {
  SearchAizProvider,
  useDataSources,
  useAccessibleSourceIds,
} from './providers';

// Hooks
export {
  useSearchAiz,
  useSearchAizSettings,
  SearchAizSettingsProvider,
  useSearchAizSetting,
  useAiSettings,
  useSourceSettings,
  useUiSettings,
  useResultSettings,
} from './hooks';

// Core UI components
export {
  CommandPalette,
  useCommandPalette,
  InlineSearch,
  useInlineSearch,
  FullSearchModal,
  useFullSearchModal,
} from './core';

// Tiles
export {
  BaseTile,
  ScoreBadge,
  SourceBadge,
  createTile,
  DocumentTile,
  ProductTile,
  CustomerTile,
  CallTile,
  EntityTile,
  PageTile,
  SupportTile,
  RealtimeTile,
  OperationsTile,
  MetricTile,
  WebTile,
  TILE_REGISTRY,
  getTileComponent,
  renderResultTile,
} from './tiles';

// Utilities
export {
  // Data source registry
  DATA_SOURCE_REGISTRY,
  getDataSource,
  getAllDataSources,
  getDataSourcesByCategory,
  getDataSourcesByPattern,
  getDataSourcesWithReranking,
  getDataSourcesWithAnalysis,
  hasAccessToSource,
  filterAccessibleSources,
  getCollectionsForSources,
  getBestPatternForSources,
  canRerankSources,
  canAnalyzeSources,
  getSourceIcon,
  getSourceIconColor,
  getSourceBadgeColor,
  DEFAULT_SOURCES_BY_CONTEXT,
  getDefaultSourcesForContext,

  // Settings resolver
  DEFAULT_SETTINGS,
  DEFAULT_PORTAL_PERMISSIONS,
  DEFAULT_DEVELOPER_PERMISSIONS,
  resolveSettings,
} from './utils';

// API client
export {
  searchClient,
  quickSearch,
  fullSearch,
  conversationalSearch,
  SearchApiError,
} from './api';

// Types (re-export everything)
export type {
  // Modes and contexts
  SearchMode,
  SearchContext,
  SearchPattern,

  // Data sources
  DataSourceCategory,
  DataSourceDefinition,
  BillingTier,

  // Results
  SearchResult,
  ResultType,
  TextHighlight,
  DocumentResult,
  RepositoryResult,
  CustomerResult,
  ProductResult,
  SupportResult,
  CallResult,
  EntityResult,
  PageResult,
  MetricResult,

  // AI
  RerankConfig,
  AnalysisConfig,
  AiAnalysis,
  ConversationTurn,

  // Settings
  SettingsLayer,
  SettingPermission,
  LayerPermissions,
  SearchAizSettings,
  PartialSettings,
  LayeredSettings,

  // API
  SearchRequest,
  SearchResponse,
  SearchFilters,
  FacetCount,

  // Tiles
  TileDisplayMode,
  TileAction,
  BaseTileProps,

  // Components
  SearchAizProps,
  SearchAizProviderProps,
  SearchState,
  SearchActions,
} from './types';
